/**
 * A person's week, on their panel in Settings › Users & roles.
 *
 * `company-and-organization.md` §5.5 names four tabs on a person — Profile,
 * Access, Activity, Hours — and this is the fourth. It is the office's half of
 * the screen a crew member has on their own phone: the same week, from the
 * same `GET /crew-app/my-hours`, which has always taken a `userId` and gated
 * it on `employees`. What it adds is the reason somebody opens it from here:
 * a punch nobody closed, which is the lead's to fix (§G.1) and is invisible
 * until somebody looks.
 *
 * Deliberately read-only. A punch is corrected on the iPad, by the lead, as a
 * new immutable event — §4.3's rule that nothing in a ledger is edited in
 * place. A second door into that from a settings panel is how two people
 * start correcting the same day.
 */
import { useCallback, useEffect, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { request } from '../../lib/api.js'
import { Segmented } from '../primitives.js'
import {
  clockTime, hoursLabel, shiftWeeks, shortDuration, weekLabel, weekdayLabel,
  type HoursDay, type HoursPayload,
} from '../../lib/hours.js'

export function PersonHours({ personId, first }: { personId: string; first: string }) {
  const [period, setPeriod] = useState<'this' | 'last'>('this')
  const [data, setData] = useState<HoursPayload | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setData(null)
    try {
      const week = period === 'last' ? shiftWeeks(new Date(), -1) : undefined
      const query = new URLSearchParams({ userId: personId })
      if (week) query.set('week', week)
      setData(await request<HoursPayload>(`/crew-app/my-hours?${query.toString()}`))
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Those hours could not load.')
    }
  }, [personId, period])

  useEffect(() => { void load() }, [load])

  const open = data?.days.filter((day) => day.open).length ?? 0

  return (
    <div className="flex flex-col gap-3 px-[18px] py-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <b className="text-[13.5px] text-text">
          {data ? weekLabel(data.week) : 'Hours'}
        </b>
        <span className="min-w-0 truncate text-[12px] text-text-muted">
          from the punches · corrected on the iPad, never here
        </span>
        <span className="ml-auto shrink-0">
          <Segmented
            label="Which week"
            value={period}
            onChange={setPeriod}
            options={[
              { value: 'this' as const, label: 'This week' },
              { value: 'last' as const, label: 'Last week' },
            ]}
          />
        </span>
      </div>

      {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}

      {data && (
        <>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="tnum text-[26px] font-bold leading-none text-text">
              {hoursLabel(data.totalMinutes)}
            </span>
            <span className="text-[12.5px] text-text-muted">
              of {data.standardHours}
              {data.crew && <> · {data.crew}</>}
            </span>
            {data.openSince && (
              <span className="ml-auto text-[12.5px] font-semibold text-accent">
                ● Clocked in since {clockTime(data.openSince)}
                {data.openAt && <span className="font-normal text-text-muted"> · {data.openAt}</span>}
              </span>
            )}
          </div>

          {/*
            * §G.1: a punch not closed by 11:59 pm local is flagged, never
            * auto-closed. Saying so here is the whole reason the office opens
            * this tab — the day stays at "in progress" and nobody downstream
            * knows the number is not final.
            */}
          {open > 0 && !data.openSince && (
            <p className="m-0 rounded-field bg-warn-bg px-2 py-1.5 text-[12px] text-text">
              {open === 1 ? 'A day' : `${open} days`} left open. {data.leadName
                ? `${data.leadName} can close it on the iPad.`
                : 'Their crew has no lead set, so the office has to close it.'}
            </p>
          )}

          {data.days.length === 0
            ? (
              <p className="m-0 text-[12.5px] text-text-faint">
                Nothing this week. Hours appear as soon as {first} clocks in on a truck.
              </p>
            )
            : data.days.map((day, index) => <DayRow key={day.date} day={day} first={index === 0} />)}
        </>
      )}
    </div>
  )
}

function DayRow({ day, first }: { day: HoursDay; first: boolean }) {
  const detail = [
    day.breakMinutes > 0 ? `Lunch ${day.breakMinutes} min` : null,
    day.stops > 0 ? `${day.stops} ${day.stops === 1 ? 'stop' : 'stops'}` : null,
  ].filter(Boolean).join(' · ')

  return (
    <div className={cn('flex items-center gap-3 py-2', !first && 'border-t border-line-faint')}>
      <span className="w-[36px] shrink-0 text-[12.5px] font-semibold text-text">
        {weekdayLabel(day.date)}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-[12.5px] text-text">
          {clockTime(day.firstIn)} – {day.open ? 'now' : clockTime(day.lastOut)}
        </span>
        {day.correction
          ? (
            <span className="block truncate text-[11.5px] text-danger">
              {day.correction.reason ?? 'Punch corrected'}
              {day.correction.byName && ` — fixed by ${day.correction.byName}`}
            </span>
          )
          : (
            <span className="block truncate text-[11.5px] text-text-muted">
              {day.open ? `${detail ? `${detail} · ` : ''}in progress` : detail || 'No stops'}
            </span>
          )}
      </span>

      <span className="tnum shrink-0 text-[12.5px] font-semibold text-text">
        {day.open ? shortDuration(day.minutes) : hoursLabel(day.minutes)}
      </span>
    </div>
  )
}
